import React, { memo, useCallback, useMemo } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InlineImageGalleryProps {
  imageUrls: string[];
  onImagePress: (imageUrls: string[], index: number) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BORDER_RADIUS = 12;
const GAP = 8;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ImageTileProps {
  uri: string;
  style: object | object[];
  onPress: () => void;
  overlay?: React.ReactNode;
}

const ImageTile: React.FC<ImageTileProps> = memo(({ uri, style, onPress, overlay }) => (
  <TouchableOpacity
    style={[styles.tileBase, style]}
    onPress={onPress}
    activeOpacity={0.8}
  >
    <Image
      source={{ uri }}
      style={styles.tileImage}
      resizeMode="cover"
    />
    {overlay}
  </TouchableOpacity>
));

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const InlineImageGalleryInner: React.FC<InlineImageGalleryProps> = ({
  imageUrls,
  onImagePress,
}) => {
  const { colors } = useThemeColors();
  const count = imageUrls.length;

  // Memoised press handlers per index to avoid re-renders
  const handlePress = useCallback(
    (index: number) => {
      onImagePress(imageUrls, index);
    },
    [imageUrls, onImagePress],
  );

  // -------------------------------------------------------------------------
  // Swipe gesture for single-image layout
  // -------------------------------------------------------------------------
  const translateX = useSharedValue(0);

  const swipeGesture = useMemo(
    () =>
      Gesture.Pan()
        .enabled(count === 1 && Platform.OS !== 'web')
        .activeOffsetX([-10, 10])
        .failOffsetY([-15, 15])
        .onUpdate((e) => {
          'worklet';
          // Rubber-band: reduce movement to give a resist-and-snap-back feel
          translateX.value = e.translationX * 0.25;
        })
        .onEnd(() => {
          'worklet';
          translateX.value = withSpring(0, { damping: 20, stiffness: 200 });
        }),
    [count, translateX],
  );

  const swipeAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  if (count === 0) return null;

  // --- 1 image: full-width, 300px max-height, swipeable ---
  if (count === 1) {
    return (
      <GestureDetector gesture={swipeGesture}>
        <Animated.View style={[styles.singleWrapper, swipeAnimatedStyle]}>
          <ImageTile
            uri={imageUrls[0]}
            style={styles.singleImage}
            onPress={() => handlePress(0)}
          />
        </Animated.View>
      </GestureDetector>
    );
  }

  // --- 2 images: side-by-side ---
  if (count === 2) {
    return (
      <View style={styles.row}>
        <ImageTile
          uri={imageUrls[0]}
          style={styles.dualImage}
          onPress={() => handlePress(0)}
        />
        <ImageTile
          uri={imageUrls[1]}
          style={styles.dualImage}
          onPress={() => handlePress(1)}
        />
      </View>
    );
  }

  // --- 3+ images ---
  // First image: full-width (200px tall)
  // Remaining: 2-up row (120px tall), "+N more" overlay on last tile if >4 total
  const remainingUrls = imageUrls.slice(1);
  // Show at most 2 thumbnails in the bottom row
  const visibleThumbs = remainingUrls.slice(0, 2);
  // Images beyond 4 total are hidden under the "+N" overlay
  const hiddenCount = count > 3 ? count - 3 : 0;

  return (
    <View style={styles.multiWrapper}>
      {/* Hero image */}
      <ImageTile
        uri={imageUrls[0]}
        style={styles.heroImage}
        onPress={() => handlePress(0)}
      />
      {/* Thumbnail row */}
      <View style={styles.row}>
        {visibleThumbs.map((uri, i) => {
          const originalIndex = i + 1;
          const isLastThumb = i === visibleThumbs.length - 1;
          const showOverlay = isLastThumb && hiddenCount > 0;

          const overlay = showOverlay ? (
            <View style={styles.moreOverlay} pointerEvents="none">
              <Text style={[styles.moreText, { color: colors.text.primary }]}>+{hiddenCount}</Text>
            </View>
          ) : undefined;

          return (
            <ImageTile
              key={uri + originalIndex}
              uri={uri}
              style={styles.thumbImage}
              onPress={() => handlePress(originalIndex)}
              overlay={overlay}
            />
          );
        })}
      </View>
    </View>
  );
};

export const InlineImageGallery = memo(InlineImageGalleryInner);
InlineImageGallery.displayName = 'InlineImageGallery';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  // Single image
  singleWrapper: {
    width: '100%',
  },
  singleImage: {
    width: '100%',
    height: 300,
    borderRadius: BORDER_RADIUS,
    overflow: 'hidden',
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },

  // Dual (2-up row)
  row: {
    flexDirection: 'row',
    gap: GAP,
  },
  dualImage: {
    flex: 1,
    height: 180,
    borderRadius: BORDER_RADIUS,
    overflow: 'hidden',
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },

  // 3+ layout
  multiWrapper: {
    gap: GAP,
  },
  heroImage: {
    width: '100%',
    height: 200,
    borderRadius: BORDER_RADIUS,
    overflow: 'hidden',
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  thumbImage: {
    flex: 1,
    height: 120,
    borderRadius: BORDER_RADIUS,
    overflow: 'hidden',
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },

  // Shared tile base (TouchableOpacity needs overflow:hidden and borderRadius)
  tileBase: {
    overflow: 'hidden',
    borderRadius: BORDER_RADIUS,
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  tileImage: {
    width: '100%',
    height: '100%',
  },

  // "+N more" overlay
  moreOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreText: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
  },
});
